import React from "react";
import CountryDropdown from "../../components/dropdowns/reactselect/CountryDropdown";
import StateDropdown from "../../components/dropdowns/reactselect/StateDropdown";
import LabelledInput from "../../components/LabelledInput";
import RequiredTextInput from "components/ValidatedFields/RequiredTextInput";
import { type CompanyInfo } from "../models";
import { useValidateIsNotNull } from "formvalidation/hooks";
import { getCountryFormDefinition } from "shared/utils/cmsUtils";
import { isNullOrEmptyString } from "shared/utils";
import { InputBase } from "components/Atoms/formInputs/InputBase";

interface Props {
  session: server.dto.ClientOnboardingSession
  companyInfo: CompanyInfo
  onCompanyInfoChange: (companyInfo: Partial<CompanyInfo>) => void
}

const CompanyInformation = (props: Props) => {
  const { name, addressLine1, addressLine2, city, state, postalCode, country } = props.companyInfo;

  const countryValidator = useValidateIsNotNull({
    key: "country",
    title: RESX.Address.Country,
    value: country
  });

  const formDefinition = country ? getCountryFormDefinition(country.key, serverReference) : undefined;

  return <div className="flex-column gap-large">
    <div>
      <LabelledInput
        required
        label={RESX.Company.CompanyName}
        id={"companyName"}
      >
        <div>
          <RequiredTextInput
            value={name}
            onChange={(newName) => { props.onCompanyInfoChange({ name: newName }); }}
            maxLength={serverReference.constants.company.maxNameLength}
            validatorKey="companyName"
          />
        </div>
      </LabelledInput>
    </div>

    <div>
      <LabelledInput
        required
        label={RESX.Address.AddressLine + " 1"}
        id="addressLine1"
      >
        <RequiredTextInput
          value={addressLine1}
          onChange={(newAddressLine1) => { props.onCompanyInfoChange({ addressLine1: newAddressLine1 }); }}
          maxLength={serverReference.constants.address.maxAddressLineLength}
          validatorKey="addressLine1"
        />
      </LabelledInput>
    </div>
    <div>
      <LabelledInput
        required={false}
        label={RESX.Address.AddressLine + " 2"}
        id="addressLine2"
      >
        <div>
          <InputBase
            id="addressLine2"
            maxLength={serverReference.constants.address.maxAddressLineLength}
            value={addressLine2}
            onChange={value => { props.onCompanyInfoChange({ addressLine2: value }); }}
          />
        </div>
      </LabelledInput>
    </div>

    <div>
      <LabelledInput
        label={RESX.Address.PostalCode}
        id="postalCode"
        required={false}
      >
        <InputBase
          id="postalCode"
          maxLength={serverReference.constants.address.maxPostalCodeLength}
          value={postalCode}
          onChange={value => { props.onCompanyInfoChange({ postalCode: value }); }}
        />
      </LabelledInput>
    </div>

    <div>
      <LabelledInput
        label={RESX.Address.City}
        id="city"
        required
      >
        <RequiredTextInput
          value={city}
          onChange={(value) => { props.onCompanyInfoChange({ city: value }); }}
          maxLength={serverReference.constants.address.maxCityLength}
          validatorKey="city"
        />
      </LabelledInput>
    </div>

    <div>
      <LabelledInput
        label={RESX.Address.Country}
        id="country"
        required
        innerRef={countryValidator.ref}
      >
        <CountryDropdown
          value={country}
          onChange={(newCountry) => {
            props.onCompanyInfoChange({
              country: newCountry,
              state: newCountry.states?.find(s => s.isCountryDefault)
            });
          }}
          config={{
            hasError: countryValidator.error.hasError,
          }}
        />
      </LabelledInput>
    </div>

    {(country != null) && <div>
      <LabelledInput
        label={RESX.Address.State}
        id="state"
        required={false}
      >
        <StateDropdown
          value={state}
          onChange={(newState) => { props.onCompanyInfoChange({ state: newState }); }}
          options={country.states}
        />
      </LabelledInput>
    </div>}

    {formDefinition?.euVatNumber.displayInUI && <div>
      <LabelledInput
        required={false}
        label={isNullOrEmptyString(formDefinition.euVatNumber.sublabel)
          ? formDefinition.euVatNumber.label
          : `${formDefinition.euVatNumber.label} (${formDefinition.euVatNumber.sublabel})`
        }
        id="euVatNumber"
      >
        <div>
          <InputBase
            id="euVatNumber"
            maxLength={serverReference.constants.company.maxEuVatNumberLength}
            value={props.companyInfo.euVatNumber}
            onChange={value => { props.onCompanyInfoChange({ euVatNumber: value }); }}
          />
        </div>
      </LabelledInput>
    </div>}
  </div>;
};

export default CompanyInformation;